import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { posApiFetch } from "@/utils/pos-api";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Loader2 } from "lucide-react";

// --- Form state ---

interface ModifierFormState {
  name: string;
  displayName: string;
  priceAdjustmentUsd: string;
  isDefault: boolean;
  available: boolean;
  displayOrder: string;
}

const EMPTY_FORM: ModifierFormState = {
  name: "",
  displayName: "",
  priceAdjustmentUsd: "0",
  isDefault: false,
  available: true,
  displayOrder: "0",
};

// --- Component ---

interface ModifierEditorProps {
  groupId: string;
  modifierId?: string;
  onBack: () => void;
}

export default function ModifierEditor({ groupId, modifierId, onBack }: ModifierEditorProps) {
  const isEdit = !!modifierId;
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<ModifierFormState>(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);

  // --- Fetch modifier group name for display ---

  const { data: modifierGroup } = useQuery<any>({
    queryKey: ["/v1/pos/modifier-groups", groupId],
    queryFn: async () => {
      const res = await posApiFetch(`/v1/pos/modifier-groups/${groupId}`);
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !!groupId,
  });

  // --- Fetch existing modifier for edit ---

  const { data: existingModifier, isLoading: isLoadingModifier } = useQuery<any>({
    queryKey: ["/v1/pos/modifiers", modifierId],
    queryFn: async () => {
      const res = await posApiFetch(`/v1/pos/modifiers/${modifierId}`);
      if (!res.ok) throw new Error("Failed to fetch modifier");
      return res.json();
    },
    enabled: isEdit,
  });

  // Populate form
  useEffect(() => {
    if (!existingModifier) return;
    const m = existingModifier;
    setForm({
      name: m.name ?? "",
      displayName: m.displayName ?? "",
      priceAdjustmentUsd: m.priceAdjustmentUsd ?? "0",
      isDefault: !!m.isDefault,
      available: m.active ?? m.available ?? true,
      displayOrder: m.displayOrder != null ? String(m.displayOrder) : "0",
    });
  }, [existingModifier]);

  // --- Field updater ---

  const set = <K extends keyof ModifierFormState>(key: K, value: ModifierFormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  // --- Submit ---

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.name.trim()) {
      toast({ title: "Validation Error", description: "Name is required", variant: "destructive" });
      return;
    }

    setIsSaving(true);
    try {
      const body: Record<string, any> = {
        name: form.name.trim(),
        displayName: form.displayName.trim() || form.name.trim(),
        priceAdjustmentUsd: form.priceAdjustmentUsd || "0",
        isDefault: form.isDefault,
        active: form.available,
        displayOrder: form.displayOrder ? Number(form.displayOrder) : 0,
      };

      let url: string;
      let method: string;

      if (isEdit) {
        url = `/v1/pos/modifiers/${modifierId}`;
        method = "PUT";
      } else {
        url = `/v1/pos/modifier-groups/${groupId}/modifiers`;
        method = "POST";
      }

      const res = await posApiFetch(url, {
        method,
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.message || `Failed to ${isEdit ? "update" : "create"} modifier`);
      }

      toast({ title: `Modifier ${isEdit ? "updated" : "created"} successfully` });
      queryClient.invalidateQueries({ queryKey: ["/v1/pos/modifier-groups"] });
      onBack();
    } catch (err: any) {
      toast({
        title: "Error",
        description: err?.message || `Failed to ${isEdit ? "update" : "create"} modifier`,
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // --- Loading ---

  if (isEdit && isLoadingModifier) {
    return (
      <div className="h-full overflow-y-auto p-4">
        <div className="flex items-center gap-2 mb-4">
          <Button variant="ghost" onClick={onBack} className="text-[13px] gap-1">
            <ArrowLeft className="h-4 w-4" /> Back to Modifiers
          </Button>
        </div>
        <div className="flex items-center justify-center min-h-[300px]">
          <Loader2 className="h-6 w-6 animate-spin text-[var(--secondary-text-color)]" />
        </div>
      </div>
    );
  }

  // --- Render ---

  return (
    <div className="h-full overflow-y-auto p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <Button variant="ghost" onClick={onBack} className="text-[13px] gap-1">
          <ArrowLeft className="h-4 w-4" /> Back to Modifiers
        </Button>
        <div className="text-center">
          <h2 className="text-[16px] font-semibold text-[var(--primary-text-color)]">
            {isEdit ? "Edit Modifier" : "Create Modifier"}
          </h2>
          {modifierGroup?.displayName && (
            <p className="text-[12px] text-[var(--secondary-text-color)]">{modifierGroup.displayName}</p>
          )}
        </div>
        <div className="w-[140px]" />
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 max-w-[900px] mx-auto pb-8">
        {/* Basic Details */}
        <Card>
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-[14px] font-semibold">Basic Details</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-[12px]">Name (Internal) *</Label>
                <Input
                  className="h-[36px] text-[13px]"
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  placeholder="e.g. medium_rare"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[12px]">Display Name</Label>
                <Input
                  className="h-[36px] text-[13px]"
                  value={form.displayName}
                  onChange={(e) => set("displayName", e.target.value)}
                  placeholder="e.g. Medium Rare"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pricing & Order */}
        <Card>
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-[14px] font-semibold">Pricing & Order</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-[12px]">Price Adjustment (USD)</Label>
                <Input
                  className="h-[36px] text-[13px]"
                  type="number"
                  step="0.01"
                  value={form.priceAdjustmentUsd}
                  onChange={(e) => set("priceAdjustmentUsd", e.target.value)}
                  placeholder="0.00"
                />
                <p className="text-[11px] text-[var(--secondary-text-color)]">
                  Use negative for discounts (e.g., -1.00)
                </p>
              </div>
              <div className="space-y-1">
                <Label className="text-[12px]">Display Order</Label>
                <Input
                  className="h-[36px] text-[13px]"
                  type="number"
                  min="0"
                  step="1"
                  value={form.displayOrder}
                  onChange={(e) => set("displayOrder", e.target.value)}
                  placeholder="0"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Options & Status */}
        <Card>
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-[14px] font-semibold">Options & Status</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-3">
            <div className="flex items-center gap-2">
              <Switch checked={form.isDefault} onCheckedChange={(v) => set("isDefault", v)} />
              <Label className="text-[12px]">Default Selection</Label>
            </div>
            <p className="text-[11px] text-[var(--secondary-text-color)]">
              {form.isDefault ? "This option will be pre-selected" : "Not pre-selected"}
            </p>
            <div className="flex items-center gap-2 pt-1">
              <Switch checked={form.available} onCheckedChange={(v) => set("available", v)} />
              <Label className="text-[12px]">Active</Label>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            className="h-[36px] text-[13px]"
            onClick={onBack}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="h-[36px] text-[13px] bg-[var(--primary-color)] text-white hover:bg-[#9f0e0e]"
            disabled={isSaving}
          >
            {isSaving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            {isEdit ? "Update Modifier" : "Create Modifier"}
          </Button>
        </div>
      </form>
    </div>
  );
}
